import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { opportunityService } from '../../services/api';

// Initial state
const initialState = {
  opportunities: [],
  opportunity: null,
  activities: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchOpportunities = createAsyncThunk(
  'opportunities/fetchOpportunities',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await opportunityService.getOpportunities(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchOpportunity = createAsyncThunk(
  'opportunities/fetchOpportunity',
  async (id, { rejectWithValue }) => {
    try {
      const response = await opportunityService.getOpportunity(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createOpportunity = createAsyncThunk(
  'opportunities/createOpportunity',
  async (data, { rejectWithValue }) => {
    try {
      const response = await opportunityService.createOpportunity(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOpportunity = createAsyncThunk(
  'opportunities/updateOpportunity',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await opportunityService.updateOpportunity(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteOpportunity = createAsyncThunk(
  'opportunities/deleteOpportunity',
  async (id, { rejectWithValue }) => {
    try {
      await opportunityService.deleteOpportunity(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchOpportunityActivities = createAsyncThunk(
  'opportunities/fetchOpportunityActivities',
  async (id, { rejectWithValue }) => {
    try {
      const response = await opportunityService.getOpportunityActivities(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Opportunity slice
const opportunitySlice = createSlice({
  name: 'opportunities',
  initialState,
  reducers: {
    clearOpportunityError: (state) => {
      state.error = null;
    },
    clearOpportunity: (state) => {
      state.opportunity = null;
      state.activities = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch opportunities
      .addCase(fetchOpportunities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOpportunities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opportunities = action.payload.results || action.payload;
        // Handle pagination if API returns pagination data
        if (action.payload.count !== undefined) {
          state.pagination.totalCount = action.payload.count;
        }
        state.error = null;
      })
      .addCase(fetchOpportunities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch opportunities';
      })
      // Fetch single opportunity
      .addCase(fetchOpportunity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOpportunity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opportunity = action.payload;
        state.error = null;
      })
      .addCase(fetchOpportunity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch opportunity';
      })
      // Create opportunity
      .addCase(createOpportunity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOpportunity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opportunities.unshift(action.payload);
        state.error = null;
      })
      .addCase(createOpportunity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create opportunity';
      })
      // Update opportunity
      .addCase(updateOpportunity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOpportunity.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the opportunity in the list if it exists
        state.opportunities = state.opportunities.map((opportunity) =>
          opportunity.id === action.payload.id ? action.payload : opportunity
        );
        // Update the current opportunity if it's the one being viewed
        if (state.opportunity && state.opportunity.id === action.payload.id) {
          state.opportunity = action.payload;
        }
        state.error = null;
      })
      .addCase(updateOpportunity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update opportunity';
      })
      // Delete opportunity
      .addCase(deleteOpportunity.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOpportunity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opportunities = state.opportunities.filter(
          (opportunity) => opportunity.id !== action.payload
        );
        // Clear the current opportunity if it's the one being deleted
        if (state.opportunity && state.opportunity.id === action.payload) {
          state.opportunity = null;
        }
        state.error = null;
      })
      .addCase(deleteOpportunity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete opportunity';
      })
      // Fetch opportunity activities
      .addCase(fetchOpportunityActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOpportunityActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload;
        state.error = null;
      })
      .addCase(fetchOpportunityActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch opportunity activities';
      });
  },
});

export const { clearOpportunityError, clearOpportunity } = opportunitySlice.actions;
export default opportunitySlice.reducer;
